<template>
    <QuickSearch />
    <div
        class="user-menu"
        v-show="showComponent"
        v-click-outside="closeMenu"
        :class="{ 'user-menu--balance-active': showBalance }"
    >
        <template v-if="!isLoggedIn">
            <a class="user-menu__registration" href="/registrace"> Vytvořit účet </a>
            <DsButton
                class="user-menu__login"
                :class="{ 'user-menu__login--loading': isLoginLoading }"
                type="primary-negative"
                size="sm"
                :is-disabled="isLoginLoading"
                @click="openLoginModal"
            >
                Přihlásit
                <div
                    v-show="isLoginLoading"
                    class="user-menu__login-loader"
                    role="status"
                    aria-label="Načítání..."
                ></div>
            </DsButton>
        </template>

        <template v-else>
            <span class="user-menu__status">
                <span class="user-menu__status-text">Přihlášen </span>
                <span class="user-menu__status-time">{{ loginDurationFormatted }}</span>
            </span>

            <a v-if="showMyBets" class="user-menu__wagers" data-test="my-wagers" :href="myBetsLink">
                <DsSystemIcon name="bet-list" swap-breakpoint="md" />
                Moje sázky
            </a>

            <button
                type="button"
                class="user-menu__deposit"
                :class="{ 'user-menu__deposit--permanent': showDeposit }"
                @click="ngAction('showDeposit')"
            >
                <DsSystemIcon name="deposit" swap-breakpoint="md" />
                Vklad
            </button>

            <UserMenuToggle
                :is-active="isUserMenuVisible"
                :user-name="userName"
                :login-duration="loginDurationFormatted"
                :show-balance="showBalance"
                :display-balance="displayBalance"
                @toggle="toggleMenu"
            />

            <UserMenuDropdown
                v-if="isLoggedIn"
                :is-open="isUserMenuVisible"
                :my-bets-link="myBetsLink"
                @myBetsClick="navigateToMyBets"
                v-model:show-balance="showBalance"
            />
        </template>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import DsButton from "@allwyn/design-system/src/button/ds-button.vue";
import DsSystemIcon from "@allwyn/design-system/src/icon/ds-system-icon.vue";
import { useMountAuthentication } from "@allwyn/ng/authentication/composables/use-mount-authentication";
import { ngAction } from "@allwyn/ng/ng-integration";
import { useNgSession } from "@allwyn/ng/ng-session";
import { vClickOutside } from "@allwyn/utils/directives/v-click-outside";
import UserMenuDropdown from "./user-menu-dropdown.vue";
import UserMenuToggle from "./user-menu-toggle.vue";
import { HeaderOptions, useHeader } from "../header/header.js";
import QuickSearch from "../quick-search/quick-search.vue";

const { isLoggedIn, session, balance, showBalance, loginDurationFormatted, isSessionStateFromCacheReady } =
    useNgSession();

const { setHeaderState, getHeaderState } = useHeader();

const isCasino = environment.nodeAliasPath?.startsWith("/hry");
const showComponent = computed(() => isSessionStateFromCacheReady.value);
const isUserMenuVisible = ref(false);

const showDeposit = isCasino;
const showMyBets = !isCasino;
const myBetsLink = "/moje-sazky";

const userName = computed(() => session.value?.PlayerName ?? "");
const displayBalance = computed(() => balance.value?.DisplayBalance);

const isLoginLoading = ref(false);

const openLoginModal = async () => {
    const loaderTimeout = setTimeout(() => {
        isLoginLoading.value = true;
    }, 150);

    await useMountAuthentication(false);
    ngAction("showLogin");

    clearTimeout(loaderTimeout);
    isLoginLoading.value = false;
};

const navigateToMyBets = () => {
    window.location.href = myBetsLink;
};

let wasMenuTransparent = false;

function resetHeaderState() {
    const nextHeaderState: HeaderOptions = {
        locked: false,
        animated: true,
    };
    if (document.documentElement.scrollTop === 0 && wasMenuTransparent) {
        nextHeaderState.transparent = true;
        nextHeaderState.active = false;
        nextHeaderState.parallax = true;
    }
    setHeaderState(nextHeaderState);
}

function toggleMenu() {
    isUserMenuVisible.value = !isUserMenuVisible.value;
}

function closeMenu() {
    isUserMenuVisible.value = false;
}

function onOpenMenu() {
    wasMenuTransparent = getHeaderState().transparent;
    document.body.classList.add("is-user-menu-open");

    setHeaderState({
        active: true,
        parallax: false,
        transparent: false,
        animated: false,
        locked: true,
    });
}

function onCloseMenu() {
    document.body.classList.remove("is-user-menu-open");
    resetHeaderState();
}

watch(isUserMenuVisible, (newValue) => {
    if (newValue) {
        onOpenMenu();
    } else {
        onCloseMenu();
    }
});

onMounted(() => {
    window.addEventListener("keydown", (e: KeyboardEvent) => e.key === "Escape" && onCloseMenu());
});

onUnmounted(() => {
    window.removeEventListener("keydown", (e: KeyboardEvent) => e.key === "Escape" && onCloseMenu());
});
</script>
